import type { MoneyLeaderboardRowDTO } from '@/services/api.service';

// ============================================================
// Section « Classement » de l'espace Statistiques (rubrique Économie) : fonctions pures, sans I/O ni store
// ============================================================
// Les soldes viennent de la base de l'argent (API Vault), lus en direct par le satellite. Le rang est
// 1 + le nombre de comptes plus riches : deux soldes égaux ont le même rang et le suivant saute.

/** Tailles de page proposées ; le satellite plafonne à 200. */
export const PAGE_SIZES = [25, 50, 100, 200] as const;

export const DEFAULT_PAGE_SIZE = 50;

/** Attente après la dernière touche avant de lancer une recherche. */
export const SEARCH_DELAY_MS = 300;

/** Un montant en dollars, chiffres groupés à la française. */
export function formatMoney(amount: number): string {
  return `${amount.toLocaleString('fr-FR')} $`;
}

/** Un mouvement d'argent signé : « +1 200 $ » ou « −300 $ ». */
export function formatSignedMoney(amount: number): string {
  if (amount > 0) return `+${formatMoney(amount)}`;
  if (amount < 0) return `\u2212${formatMoney(Math.abs(amount))}`;
  return formatMoney(0);
}

/**
 * Part de la monnaie en circulation (en pour cent) : deux décimales sous 10 %, une au-delà, « < 0,01 % »
 * pour une part trop petite pour s'écrire, « 0 % » pour un solde nul ou négatif.
 */
export function formatShare(share: number): string {
  if (!Number.isFinite(share) || share <= 0) return '0\u00a0%';
  if (share < 0.01) return '< 0,01\u00a0%';
  const digits = share < 10 ? 2 : 1;
  return `${share.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: digits })}\u00a0%`;
}

/** Couleur de la pastille de rang : or, argent et bronze pour les trois premiers rangs. */
export function medal(rank: number): 'gold' | 'silver' | 'bronze' | null {
  if (rank === 1) return 'gold';
  if (rank === 2) return 'silver';
  if (rank === 3) return 'bronze';
  return null;
}

/**
 * Part de la monnaie que détiennent les comptes classés dans les `top` premiers rangs, d'après la première
 * page du classement (sans recherche, au moins `top` lignes par page). Null pour une page qui ne commence
 * pas au premier rang. Un classement plus court que `top` compte en entier.
 */
export function topShare(rows: readonly MoneyLeaderboardRowDTO[], top = 10): number | null {
  if (rows.length === 0 || rows[0].rank !== 1) return null;
  return rows.filter((r) => r.rank <= top).reduce((sum, r) => sum + Math.max(0, r.share), 0);
}

/** « Page 2 sur 7 ». */
export function pageLabel(page: number, totalPages: number): string {
  return `Page ${page + 1} sur ${Math.max(1, totalPages)}`;
}

/**
 * Ce que la page dit d'une réponse en erreur du classement. 404 : le satellite du projet n'a pas encore la
 * route (version d'avant la 1.0.23). 403 : le grade de l'utilisateur n'a pas le droit aux statistiques
 * de l'économie. Sinon le message du satellite (base Vault absente ou injoignable).
 */
export function leaderboardError(status: number | undefined, error: string | undefined): string {
  if (status === 404) {
    return 'Le satellite de ce projet ne sert pas encore le classement : il faut le redéployer en version 1.0.23 ou plus.';
  }
  if (status === 403) return "Ton grade n'a pas accès aux statistiques de l'économie.";
  return error?.trim() || "Le classement n'a pas pu être lu.";
}

/** Même lecture pour l'historique d'un joueur, ouvert depuis le classement. */
export function historyError(status: number | undefined, error: string | undefined): string {
  if (status === 403) return "Ton grade n'a pas le droit de voir l'historique d'argent des joueurs.";
  return error?.trim() || "L'historique n'a pas pu être lu.";
}

/** Uuid raccourci, pour un joueur dont aucun pseudo n'est connu. */
export function shortUuid(uuid: string): string {
  return uuid.length > 13 ? `${uuid.slice(0, 13)}\u2026` : uuid;
}
